import styled, { keyframes } from 'styled-components';

const carBounce = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-2px) scale(1.03); }
`;

const MOBILE_BREAKPOINT = '768px';

export const StepperWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary.dark} 0%, ${({ theme }) => theme.colors.primary.main} 100%);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(59, 89, 152, 0.2);

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 8px 10px;
    margin-left: -8px;
    margin-right: -8px;
    border-radius: 0;
  }
`;

export const DeliveryIdLabel = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.25);
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  text-align: left;
`;

export const StepperInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

export const StepperTrack = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: 4px;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const StepNode = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 1;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 4px;
  color: inherit;
  font: inherit;
  transition: opacity 0.2s;
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    opacity: 0.9;
  }
  &:active {
    opacity: 0.95;
  }
  &:disabled {
    cursor: default;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex: 0 0 auto;
    min-width: 56px;
    padding: 6px 4px;
  }
`;

export const NodeCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $active, $passed }) =>
    $active ? '#fff' : $passed ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)'};
  border: 2px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 11px;
  color: ${({ $active, theme }) => ($active ? theme.colors.primary.main : '#fff')};
  transition: background 0.3s, transform 0.2s;
  flex-shrink: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 36px;
    height: 36px;
    font-size: 12px;
  }
`;

export const CarNode = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fff;
  color: ${({ theme }) => theme.colors.primary.main};
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${carBounce} 2s ease-in-out infinite;
  flex-shrink: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 40px;
    height: 40px;
  }
`;

export const StepLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  text-align: center;
  line-height: 1.2;
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 9px;
    max-width: 52px;
  }
`;

export const SegmentWrapper = styled.span`
  flex: 0 0 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex: 0 0 16px;
  }
`;

export const Segment = styled.span`
  display: inline-block;
  width: 12px;
  height: 3px;
  background: ${({ $passed }) => ($passed ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.25)')};
  border-radius: 2px;
  transition: background 0.3s;
  margin-right: ${({ $showArrow }) => ($showArrow ? '2px' : '0')};

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 8px;
  }
`;

export const SegmentArrow = styled.span`
  display: inline-block;
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 8px solid rgba(255,255,255,0.95);
  vertical-align: middle;
  filter: drop-shadow(0 0 1px rgba(0,0,0,0.2));

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    border-top-width: 4px;
    border-bottom-width: 4px;
    border-left-width: 6px;
  }
`;

export const CancelAction = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.colors.danger.main};
  background: ${({ theme }) => theme.colors.danger.main};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 2px 6px rgba(252, 80, 80, 0.35);
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: ${({ theme }) => theme.colors.danger.dark};
    border-color: ${({ theme }) => theme.colors.danger.dark};
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(252, 80, 80, 0.45);
  }
  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: 44px;
    padding: 10px 16px;
    font-size: 13px;
  }
`;
